import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import apiClient from '../api/axios';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
    sub: string;
    exp: number;
    role?: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    isAdmin: boolean;
    token: string | null;
    companyId: number | null;
    isLoading: boolean;
    login: (token: string, companyId: number) => void;
    adminLogin: (token: string, companyId: number) => void; // Placeholder, will be used by LoginPage
    logout: (options?: { sessionExpired?: boolean }) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [token, setToken] = useState<string | null>(null);
    const [companyId, setCompanyId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const logout = useCallback((options: { sessionExpired?: boolean } = {}) => {
        if (!isAuthenticated) return;

        if (options.sessionExpired) {
            alert("セッションが切れました。再度ログインしてください。");
        }

        // 全てのlocalStorageをクリア（他のユーザーのデータが残らないように）
        localStorage.clear();
        sessionStorage.clear();

        setToken(null);
        setCompanyId(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
    }, [isAuthenticated]);

    useEffect(() => {
        const validateToken = async () => {
            const storedToken = localStorage.getItem('authToken');
            const storedCompanyId = localStorage.getItem('companyId');

            if (storedToken && storedCompanyId) {
                try {
                    // トークンをデコードしてロールを確認
                    const decodedToken = jwtDecode<DecodedToken>(storedToken);
                    if (decodedToken.exp * 1000 < Date.now()) {
                        throw new Error("Token expired");
                    }

                    await apiClient.get('/api/auth/keep-alive');
                    setToken(storedToken);
                    setCompanyId(parseInt(storedCompanyId, 10));
                    setIsAuthenticated(true);
                    if (decodedToken.role === 'admin') {
                        setIsAdmin(true);
                    } else {
                        setIsAdmin(false);
                    }
                } catch (error) {
                    console.error("Token validation failed on startup:", error);
                    logout({ sessionExpired: true });
                }
            }
            setIsLoading(false);
        };

        validateToken();
    }, [logout]);

    useEffect(() => {
        const handleUnauthorized = () => {
            logout({ sessionExpired: true });
        };

        window.addEventListener('unauthorized', handleUnauthorized);

        return () => {
            window.removeEventListener('unauthorized', handleUnauthorized);
        };
    }, [logout]);

    // 複数タブでの異なるユーザーログイン検知
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            // 別タブでcompanyIdが変更された（別ユーザーがログイン）
            if (e.key === 'companyId' && e.newValue !== null && e.newValue !== companyId?.toString()) {
                alert('別のタブで異なるユーザーがログインしました。このタブからログアウトします。');
                logout();
                window.location.href = '/login';
            }

            // 別タブでログアウトされた（authTokenが削除された）
            if (e.key === 'authToken' && e.newValue === null && isAuthenticated) {
                logout();
                window.location.href = '/login';
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [companyId, isAuthenticated, logout]);

    const login = (newToken: string, newCompanyId: number) => {
        // ログイン時に既存のキャッシュをクリア（前のユーザーのデータを削除）
        localStorage.clear();
        sessionStorage.clear();

        // 新しい認証情報を保存
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('companyId', newCompanyId.toString());
        setToken(newToken);
        setCompanyId(newCompanyId);
        setIsAuthenticated(true);
        setIsAdmin(false);
    };

    const adminLogin = (newToken: string, newCompanyId: number) => {
        // ログイン時に既存のキャッシュをクリア（前のユーザーのデータを削除）
        localStorage.clear();
        sessionStorage.clear();

        // 新しい認証情報を保存
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('companyId', newCompanyId.toString());
        setToken(newToken);
        setCompanyId(newCompanyId);
        setIsAuthenticated(true);
        setIsAdmin(true);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, isAdmin, token, companyId, isLoading, login, adminLogin, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
